import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Loader2, Receipt, Filter, X, Truck } from "lucide-react";
import { toast } from "sonner";
import NewPurchaseDialog from "@/components/NewPurchaseDialog";
import QuickSupplierDialog from "@/components/QuickSupplierDialog";
import useViewMode from "@/hooks/useViewMode";
import ViewToggle from "@/components/ViewToggle";

const emptyFilter = {
  supplier_id: "",
  material_type: "",
  material_id: "",
  warehouse: "",
  date_from: "",
  date_to: "",
  min_price: "",
  max_price: "",
};

export default function Purchases() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(emptyFilter);
  const [showFilter, setShowFilter] = useState(false);
  const [open, setOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [view, setView] = useViewMode("purchases", "list");

  const load = async () => {
    setLoading(true);
    try {
      const q = Object.fromEntries(
        Object.entries(filter).filter(([, v]) => v !== "" && v !== null)
      );
      const [p, s] = await Promise.all([
        api.get("/purchases", { params: q }),
        api.get("/suppliers"),
      ]);
      setItems(p.data);
      setSuppliers(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const applyFilter = () => { load(); setShowFilter(false); };
  const clearFilter = () => { setFilter(emptyFilter); setTimeout(() => load(), 0); };

  const totals = useMemo(() => {
    const spent = items.reduce((s, p) => s + p.total_price, 0);
    return { count: items.length, spent };
  }, [items]);

  const remove = async (p) => {
    if (!window.confirm(`${p.purchase_no} silinecek ve stok geri alınacak. Devam?`)) return;
    try {
      await api.delete(`/purchases/${p.id}`);
      toast.success("Alım silindi, stok düzeltildi");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const fmt = (n) => Number(n).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return "-"; }
  };

  return (
    <div>
      <PageHeader
        title="Alışlar"
        subtitle={`${totals.count} alım · ₺${fmt(totals.spent)} toplam harcama`}
        breadcrumbs={["Modüller", "Alışlar"]}
        actions={
          <div className="flex gap-2">
            <Button
              data-testid="toggle-filter-btn"
              onClick={() => setShowFilter((v) => !v)}
              variant="outline"
              className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              <Filter className="w-4 h-4 mr-2" /> Filtre
            </Button>
            <Button
              data-testid="add-supplier-btn"
              onClick={() => setSupplierOpen(true)}
              variant="outline"
              className="border-slate-700 bg-transparent text-emerald-300 hover:bg-emerald-950/40 rounded-sm uppercase tracking-wider text-xs font-semibold"
            >
              <Truck className="w-4 h-4 mr-2" /> Yeni Tedarikçi
            </Button>
            <Button disabled={!(can("purchases", "add") && can("warehouse", "edit"))} title={!(can("purchases", "add") && can("warehouse", "edit")) ? "Alış oluşturma yetkiniz yok (purchases.add + warehouse.edit)" : undefined}
              data-testid="add-purchase-btn"
              onClick={() => setOpen(true)}
              className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" /> Yeni Alım
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        {showFilter && (
          <Card className="bg-slate-900 border-slate-800 rounded-sm p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</Label>
                <Select value={filter.supplier_id || "all"} onValueChange={(v) => setFilter({ ...filter, supplier_id: v === "all" ? "" : v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm h-9"><SelectValue placeholder="Hepsi" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="all">Hepsi</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Malzeme Tipi</Label>
                <Select value={filter.material_type || "all"} onValueChange={(v) => setFilter({ ...filter, material_type: v === "all" ? "" : v, material_id: "" })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm h-9"><SelectValue placeholder="Hepsi" /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="all">Hepsi</SelectItem>
                    <SelectItem value="fabric">Kumaş</SelectItem>
                    <SelectItem value="accessory">Aksesuar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Başlangıç</Label>
                <Input type="date" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Bitiş</Label>
                <Input type="date" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Depo</Label>
                <Input value={filter.warehouse} onChange={(e) => setFilter({ ...filter, warehouse: e.target.value })} placeholder="R-01" className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Min Fiyat (₺)</Label>
                <Input type="number" step="0.01" value={filter.min_price} onChange={(e) => setFilter({ ...filter, min_price: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Max Fiyat (₺)</Label>
                <Input type="number" step="0.01" value={filter.max_price} onChange={(e) => setFilter({ ...filter, max_price: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
              </div>
              <div className="flex items-end gap-2">
                <Button data-testid="apply-filter-btn" onClick={applyFilter} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold flex-1">Uygula</Button>
                <Button variant="outline" onClick={clearFilter} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm"><X className="w-4 h-4" /></Button>
              </div>
            </div>
          </Card>
        )}

        <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800">
            <div className="text-[11px] text-slate-500 font-mono">
              {loading ? "" : `${totals.count} kayıt`}
            </div>
            <ViewToggle value={view} onChange={setView} testId="purchases-view" />
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 px-6">
              <Receipt className="w-10 h-10 mx-auto text-slate-700 mb-3" />
              <div className="text-slate-400 text-sm">Kriterlere uyan alım bulunamadı.</div>
            </div>
          ) : view === "grid" ? (
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="purchases-cards">
              {items.map((p) => (
                <div
                  key={p.id}
                  data-testid={`purchase-card-${p.id}`}
                  className="group relative rounded-sm border border-slate-800 bg-slate-950/40 p-3 hover:bg-slate-900/60 hover:border-slate-700 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-mono text-sm text-blue-400 font-bold">{p.purchase_no}</div>
                    <span className="text-[10px] font-mono text-slate-500">{fmtDate(p.purchase_date)}</span>
                  </div>
                  <div className="text-sm text-slate-100 font-semibold truncate">{p.supplier_name}</div>
                  <div className="text-[11px] text-slate-400 truncate">{p.material_name}</div>
                  <div className="text-[10px] font-mono text-slate-600 mb-2">
                    {p.material_code} · {p.material_type === "fabric" ? "Kumaş" : "Aksesuar"}
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-[11px] mb-2">
                    <div className="text-slate-500 font-mono">Miktar</div>
                    <div className="text-right text-slate-100 font-mono tabular-nums">
                      {fmt(p.quantity)} <span className="text-slate-500 text-[9px]">{p.unit}</span>
                    </div>
                    <div className="text-slate-500 font-mono">Birim</div>
                    <div className="text-right text-slate-300 font-mono tabular-nums">₺{fmt(p.unit_price)}</div>
                    <div className="text-slate-500 font-mono">Toplam</div>
                    <div className="text-right text-slate-50 font-mono font-bold tabular-nums">₺{fmt(p.total_price)}</div>
                    {p.warehouse_location && (
                      <>
                        <div className="text-slate-500 font-mono">Depo</div>
                        <div className="text-right text-slate-400 font-mono">{p.warehouse_location}</div>
                      </>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                    <div className="text-[10px] font-mono text-slate-500 truncate flex-1">
                      {p.invoice_no || "—"}
                    </div>
                    <Button disabled={!(can("purchases", "delete"))} title={!(can("purchases", "delete")) ? "Alış silme yetkiniz yok (purchases.delete)" : undefined}
                      data-testid={`delete-purchase-card-${p.id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(p)}
                      className="h-6 w-6 p-0 text-slate-500 hover:text-red-400 hover:bg-red-950/40"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-slate-800">
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Alım No</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tarih</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Malzeme</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Miktar</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birim ₺</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Toplam ₺</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Fatura / İrsaliye</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Depo</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="border-slate-800 hover:bg-slate-800/40" data-testid={`purchase-row-${p.id}`}>
                    <TableCell className="font-mono text-xs text-blue-500 font-semibold">{p.purchase_no}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-300">{fmtDate(p.purchase_date)}</TableCell>
                    <TableCell className="text-slate-100 text-sm">{p.supplier_name}</TableCell>
                    <TableCell>
                      <div className="text-slate-100 text-sm font-medium">{p.material_name}</div>
                      <div className="text-[10px] font-mono text-slate-500 mt-0.5">{p.material_code} · {p.material_type === "fabric" ? "Kumaş" : "Aksesuar"}</div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-slate-100 tabular-nums">{fmt(p.quantity)} <span className="text-slate-500 text-[10px]">{p.unit}</span></TableCell>
                    <TableCell className="text-right font-mono text-slate-300 tabular-nums">{fmt(p.unit_price)}</TableCell>
                    <TableCell className="text-right font-mono text-slate-50 font-semibold tabular-nums">{fmt(p.total_price)}</TableCell>
                    <TableCell className="text-xs">
                      <div className="text-slate-400 font-mono">{p.invoice_no || "-"}</div>
                      <div className="text-slate-600 font-mono">{p.delivery_note_no || ""}</div>
                    </TableCell>
                    <TableCell className="text-xs font-mono text-slate-400">{p.warehouse_location || "-"}</TableCell>
                    <TableCell className="text-right">
                      <Button disabled={!(can("purchases", "delete"))} title={!(can("purchases", "delete")) ? "Alış silme yetkiniz yok (purchases.delete)" : undefined}
                        data-testid={`delete-purchase-${p.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(p)}
                        className="text-slate-500 hover:text-red-400 hover:bg-red-950/30 rounded-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <NewPurchaseDialog
        open={open}
        onOpenChange={setOpen}
        onSuccess={load}
      />
      <QuickSupplierDialog
        open={supplierOpen}
        onOpenChange={setSupplierOpen}
        onCreated={() => { setSupplierOpen(false); load(); }}
      />
    </div>
  );
}
